import { useEffect, useCallback } from "react";

import { runInAction } from "mobx";

import { jumpToLabel } from "@/components/suggestions/actions/jumpToLabel";
import { groupsJson } from "@/components/suggestions/providers/data";
import { operatorSuggestionsData } from "@/components/suggestions/providers/operatorSuggestionsProvider";
import { servedSuggestions } from "@/components/suggestions/providers/utils/servedSuggestions";
import { trackedHintType } from "@/components/suggestions/types";
import {
  isMathConversionSuggestion,
  searchSuggestions,
  sortSuggestions,
  splitInlineEditable,
  transformSuggestion,
} from "@/components/suggestions/utils";
import { triggerKeyboardInput } from "@/input/handleKeyboardInput";
import { acceptPhantomHint, applySuggestion } from "@/input/utils/editOperators";
import { cursorIsActive } from "@/models/CursorModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { isGroupClosingSymbol, isGroupOpeningSymbol } from "@/models/nodes/groups/utils";
import { isLabelOrSubscript } from "@/models/utils/semanticChecks";
import { getIsAndroid } from "@/utils/getIsAndroid";
import { getIsMobile } from "@/utils/getIsMobile";
import { getInputValueAfterKeydown, isSymbolKey } from "@/utils/input";
import { keyMatcher } from "@/utils/keyMatcher";
import { trackEvent } from "@/utils/trackEvent";

import type { SelectSuggestionFn, Suggestion } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { EditorModel } from "@/models/EditorModel";
import type { MigratedSource } from "@/models/LibrariesModel";

type NumberKey = "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9";

export const AVAILABLE_NUMBER_SHORTCUTS = Array.from({ length: 9 }).map(
  (_, i) => `${i + 1}`,
) as NumberKey[];

const AUTO_PICK_TRIGGER_KEYS = [",", ";", ":"];

function isBracketSeparatorPick(
  context: "math" | "text",
  precedingChar: string | undefined,
  key: string,
): boolean {
  return (
    context === "text" &&
    !!precedingChar &&
    isGroupOpeningSymbol(precedingChar) &&
    AUTO_PICK_TRIGGER_KEYS.includes(key)
  );
}

type Props = {
  value: string;
  editor: EditorModel;
  renderContext?: "math" | "text";
  onSelect: SelectSuggestionFn;
};

export function useSuggestionsActions({ value, editor, renderContext = "math", onSelect }: Props) {
  const isMobile = getIsMobile();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const {
        activeSuggestion,
        currentList: list,
        visible: suggestionsVisible,
      } = editor.suggestions;
      const { input, cursor, selection } = editor.doc;

      const isModifierKeyPressed = e.metaKey || e.ctrlKey;

      if (isModifierKeyPressed && AVAILABLE_NUMBER_SHORTCUTS.includes(e.key as NumberKey)) {
        stopEvent(e);

        const index = Number(e.key) - 1;

        if (list[index]) {
          onSelect(list[index], e, true);
        }

        return;
      }

      if (keyMatcher("Mod+Space", e)) {
        return;
      }

      if (suggestionsVisible) {
        if (keyMatcher("Shift+Tab", e)) {
          stopEvent(e);

          const currentHint = editor.hints.phantomHint;

          runInAction(() => {
            editor.hints.findNextPhantomHintIndex();
          });

          // Track hint cycling after the index has been updated
          const nextHint = editor.hints.phantomHint;
          if (editor.doc.id) {
            trackEvent("hints_suggestion_cycled", {
              from_hint_type: currentHint?.label,
              to_hint_type: nextHint?.label,
              doc_id: editor.doc.id,
            });
          }

          return;
        }
      }

      if ((suggestionsVisible || editor.suggestions.query) && keyMatcher("Escape", e)) {
        e.preventDefault();
        editor.suggestions.reset();
        editor.hints.setPhantomHints([]);
        if (input.editingNode) {
          editor.resetInput();
        }
        return;
      }

      // A context menu (right-click, or Mod+Space on a selection) turns into a search on the
      // first typed character: seed its field, which mounts and focuses it for the rest.
      if (
        suggestionsVisible &&
        editor.suggestions.pointerAnchor !== null &&
        !editor.suggestions.query &&
        isSymbolKey(e) &&
        e.key !== " "
      ) {
        stopEvent(e);

        runInAction(() => {
          editor.suggestions.query = e.key;
        });

        return;
      }

      if (!activeSuggestion) {
        return;
      }

      if (
        keyMatcher("Enter", e) ||
        keyMatcher("Tab", e) ||
        // For some reasons if you press "done" keyboard button on iPhone when cursor is after
        // some symbol, for example "=" or "+" it wont triggers `Enter` but `Shift+Enter`
        (keyMatcher("Shift+Enter", e) && isMobile)
      ) {
        stopEvent(e);

        const { tiptap } = editor;
        onSelect(activeSuggestion, e, true);

        if (tiptap && renderContext === "text" && !suggestionsVisible && keyMatcher("Enter", e)) {
          const { isTableCellLike } = editor.editorState;

          if (!isTableCellLike) {
            editor.resetInput(true);
            cursor.reset();
            tiptap.chain().focus(tiptap.state.selection.to).splitBlockOrListItem().run();
            return;
          }
        }

        if (keyMatcher("Tab", e)) {
          const hint = editor.hints.phantomHintResult;
          if (hint) {
            acceptPhantomHint(editor.doc, hint.children, trackedHintType(editor.hints.phantomHint));
          }
        }
        return;
      }

      if (!input.editingNode && jumpToLabel(editor.doc, activeSuggestion, onSelect, e)) {
        stopEvent(e);
        return;
      }

      if (renderContext === "math" && keyMatcher("Space", e)) {
        if (!isLabelOrSubscript(cursor.node?.parent)) {
          onSelect(activeSuggestion, e);
          return;
        }
      }

      const isSymbol = isSymbolKey(e);
      const { editingNode } = input;

      if (editingNode?.isInlineEditable()) {
        const newValue = getInputValueAfterKeydown(e);

        if (editingNode.validateValue(newValue, true)) {
          editingNode.value = newValue;
          return;
        }

        if (
          e.key === "^" &&
          editingNode.is(FunctionNodeModel) &&
          editingNode.powerPlacement === "child"
        ) {
          const { inlineNode } = editingNode;
          if (!inlineNode) return;

          stopEvent(e);
          editor.doc.transact(() => {
            input.reset(true);
            cursor.moveTo(inlineNode, 0);

            if (cursorIsActive(cursor)) {
              applySuggestion(cursor, null, {
                applyTo: "parent:power",
                cursorPlacement: 0,
              });
            }
          }, "useSuggestionsActions:editingFunctionPower");
          return;
        }

        if (isSymbol && editingNode.isSplittable) {
          let inEditMode;
          if (e.target instanceof HTMLInputElement) {
            inEditMode = splitInlineEditable(editor.doc, editingNode, e.target, e.key);
          }

          stopEvent(e);

          if (!inEditMode) {
            editor.resetInput(true);

            if (keyMatcher("Space", e)) {
              triggerKeyboardInput(editor.doc, "Space", e);
            } else {
              runInAction(() => {
                input.value = e.key;
                editor.suggestions.mode = "active";
              });
            }
          }
          return;
        }
      }

      if (editor.editorState.latexInput) {
        if (
          !activeSuggestion ||
          (activeSuggestion.type === "single" && activeSuggestion.item.displayName === "LaTeX")
        ) {
          return;
        }

        if (keyMatcher("Space", e)) {
          stopEvent(e);
          onSelect(activeSuggestion, e, true);
          triggerKeyboardInput(editor.doc, "Space", e);
        }
        return;
      }

      let nextSuggestion: Suggestion | null = null;

      const precedingChar =
        renderContext === "text" ? getCharBeforeInputValue(editor.doc, value) : undefined;

      if (
        activeSuggestion &&
        isSymbol &&
        shouldAutoPickSuggestion(
          editor.doc,
          e.key,
          value,
          renderContext,
          activeSuggestion,
          () => {
            nextSuggestion =
              sortSuggestions(e.key, searchSuggestions(e.key, editor.doc, []))[0] ?? null;
            return nextSuggestion;
          },
          precedingChar,
        )
      ) {
        if (renderContext === "text" && keyMatcher("Period", e)) {
          const { nodeBefore } = editor.tiptap?.state.selection.$from ?? {};
          if (nodeBefore?.text && /^\d+?$/.test(nodeBefore.text)) {
            return;
          }
        }
        if (renderContext === "text" && keyMatcher("Space", e)) {
          const { nodeBefore } = editor.tiptap?.state.selection.$from ?? {};
          if (nodeBefore?.text && /^\d+\.$/.test(nodeBefore.text)) {
            return;
          }
        }

        // Don't auto-pick when typing exact opening and closing punctuation in text mode
        if (
          renderContext === "text" &&
          isGroupOpeningSymbol(value) &&
          isGroupClosingSymbol(e.key)
        ) {
          return;
        }

        const hadSelectedNodes = selection.hasSelectedNodes;
        let pickedSuggestion: Suggestion | null = null;
        if (!hadSelectedNodes) {
          if (isBracketSeparatorPick(renderContext, precedingChar, e.key)) {
            runInAction(() => {
              editor.forceWrapInGroup = true;
            });
          }

          pickedSuggestion = transformSuggestion(e.key, activeSuggestion, nextSuggestion);
          onSelect(pickedSuggestion, e);
        }

        if (renderContext === "text" && keyMatcher("Space", e)) {
          stopEvent(e);
          // A math conversion commits the trailing space itself (so a single undo
          // restores it); insert it here only when nothing converted the text.
          if (!(pickedSuggestion && isMathConversionSuggestion(pickedSuggestion))) {
            triggerKeyboardInput(editor.doc, "Space", e);
          }
        }
      }
    },
    [editor, onSelect, renderContext, value, isMobile],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (renderContext !== "text") return;

    const { suggestions } = editor;
    const selectActive = () => {
      const { activeSuggestion } = suggestions;
      if (activeSuggestion) {
        onSelect(activeSuggestion, undefined, true);
      }
    };

    return suggestions.setSelectActiveHandler(selectActive);
  }, [editor, renderContext, onSelect]);

  // Chrome Android keeps the typed text in an IME composition and delivers Enter as a
  // trailing "\n" in the composition data — no Enter keydown ever fires, and the math
  // <input> drops the newline. The composition end is the only signal left; pick there.
  useEffect(() => {
    if (renderContext !== "math" || !getIsAndroid()) return;

    const handleCompositionEnd = (e: CompositionEvent) => {
      if (e.target !== editor.doc.input.inputElement || !e.data?.endsWith("\n")) return;

      const { activeSuggestion } = editor.suggestions;
      if (activeSuggestion) {
        onSelect(activeSuggestion, undefined, true);
      }
    };

    document.addEventListener("compositionend", handleCompositionEnd, { capture: true });

    return () => {
      document.removeEventListener("compositionend", handleCompositionEnd, { capture: true });
    };
  }, [editor, renderContext, onSelect]);
}

function stopEvent(e: KeyboardEvent) {
  e.preventDefault();
  e.stopPropagation();
}

function matchAutoCompletable(value: string, autoCompletable: boolean | { regex: string }) {
  if (typeof autoCompletable === "boolean") {
    return autoCompletable;
  }

  return new RegExp(autoCompletable.regex, "u").test(value);
}

// TODO: implement autocompletion for grouping operators
// (to support autocompletion in tiptap)
function suggestionCanAutocomplete(
  preceding: string,
  context: "math" | "text",
  suggestion: Suggestion,
) {
  if (suggestion.type !== "single") return false;

  if (suggestion.canAutocomplete) {
    if (typeof suggestion.canAutocomplete === "boolean") {
      return suggestion.canAutocomplete;
    }
    if (suggestion.canAutocomplete[context]) {
      return matchAutoCompletable(preceding, suggestion.canAutocomplete[context]);
    }
    return false;
  }

  return true;
}

export function suggestionCanBeAutocompleted(
  value: string,
  context: "math" | "text",
  suggestion: Suggestion,
) {
  if (typeof suggestion.canBeAutocompleted === "boolean") {
    return suggestion.canBeAutocompleted;
  }
  if (suggestion.canBeAutocompleted?.[context]) {
    return matchAutoCompletable(value, suggestion.canBeAutocompleted[context]);
  }

  return false;
}

const OPERATOR_SOURCES: MigratedSource[] = [
  "operator-symbols.json",
  "supplemental-operator-symbols.json",
];

function isOperatorSymbol(doc: DocumentModel, value: string, exactMatch?: boolean) {
  if (!value) return false;

  return servedSuggestions(doc, OPERATOR_SOURCES).some((s) => {
    if (s.item.symbol === value) return true;

    const keywords = Object.keys(s.item.keywords?.en ?? {});

    return exactMatch ? keywords.includes(value) : keywords.some((k) => k.startsWith(value));
  });
}

export function getKeywordKeys(keywords: string[] | Record<string, number> | undefined) {
  if (!keywords) return [];
  return Array.isArray(keywords) ? keywords : Object.keys(keywords);
}

function isGroupSymbol(value: string, exactMatch?: boolean) {
  if (!value) return false;

  return Object.values(groupsJson).some((s) => {
    if (s.openingSymbol.symbol === value || s.closingSymbol.symbol === value) return true;

    const { keywords } = s;
    const keywordKeys = getKeywordKeys(keywords);

    return exactMatch ? keywordKeys.includes(value) : keywordKeys.some((k) => k.startsWith(value));
  });
}

function isOperator(value: string) {
  return operatorSuggestionsData.some((s) => s.item.symbol === value || s.item.keywords?.en[value]);
}

function checkValueContainsOnlySymbols(value: string) {
  // NB: \w includes "_" (underscore), so we don't use it here
  return !/(\d|\p{Letter})/u.test(value);
}

function getCharBeforeInputValue(doc: DocumentModel, value: string): string | undefined {
  if (!value) return undefined;

  const { nodeBefore } = doc.editor.tiptap?.state.selection.$from ?? {};
  const text = nodeBefore?.text;

  if (!text || !text.endsWith(value)) return undefined;
  return text[text.length - value.length - 1];
}

function shouldAutoPickSuggestion(
  doc: DocumentModel,
  lastTypedChar: string,
  value: string,
  context: "math" | "text",
  suggestion: Suggestion,
  lookupNextSuggestion: () => Suggestion | undefined,
  precedingChar?: string,
): boolean {
  if (suggestion.type !== "single" && suggestion.type !== "multi") return false;

  if (suggestion.type === "single" && suggestion.item.type === "slashCommand") {
    return false;
  }

  const fullValue = value + lastTypedChar;

  // do not autocomplete if whole string matches operator symbol
  if (isOperatorSymbol(doc, fullValue) || isOperator(fullValue) || isGroupSymbol(fullValue)) {
    return false;
  }

  if (isBracketSeparatorPick(context, precedingChar, lastTypedChar)) {
    return true;
  }

  if (suggestionCanBeAutocompleted(fullValue, context, suggestion)) {
    return true;
  }

  // check if current operator can autocomplete preceding string
  if (checkValueContainsOnlySymbols(lastTypedChar)) {
    const nextSuggestion = lookupNextSuggestion();
    if (nextSuggestion && suggestionCanAutocomplete(value, context, nextSuggestion)) {
      return true;
    }

    if (suggestion.type === "multi" && isOperator(lastTypedChar)) {
      return true;
    }
  }

  if (checkValueContainsOnlySymbols(value)) {
    if (isOperator(value)) {
      return suggestionCanAutocomplete(value, context, suggestion);
    }

    return lastTypedChar !== " " && isOperatorSymbol(doc, value, true);
  }

  return false;
}
